"use client";

import { useState } from "react";
import { Check, ChevronRight, Copy, TableProperties } from "lucide-react";

import { useCopyToClipboard } from "../hooks/use-copy-to-clipboard";
import { TruncatedValue } from "./truncated-value";
import { cn } from "../utils";

/**
 * The JSON table projection, shared by every surface that shows a JSON
 * payload: the markdown ```json fence (`JsonCodeBlock`) and an agent tool
 * call's Arguments / Response panes (`ToolResultDetails`).
 *
 * Deliberately free of fenced-code chrome — streamdown's header, line numbers
 * and download come from the markdown pipeline's `meta` and mean nothing to a
 * tool payload, which renders its source as a truncating `<pre>` instead. What
 * both surfaces share is this table and the toggle that reaches it.
 */

export type JsonValue =
  | null
  | boolean
  | number
  | string
  | JsonValue[]
  | { [key: string]: JsonValue };

/** The parsed value, or null when the text is not JSON (so: no table view). */
export function parseJson(code: string): JsonValue | null {
  try {
    return JSON.parse(code) as JsonValue;
  } catch {
    return null;
  }
}

function isNested(value: JsonValue): value is JsonValue[] | { [key: string]: JsonValue } {
  return typeof value === "object" && value !== null;
}

function valueSummary(value: JsonValue): string {
  if (Array.isArray(value)) return `[${value.length} item${value.length === 1 ? "" : "s"}]`;
  if (isNested(value))
    return `{${Object.keys(value).length} field${Object.keys(value).length === 1 ? "" : "s"}}`;
  return JSON.stringify(value) ?? "null";
}

/** What a cell puts on the clipboard: the raw string, or the subtree as JSON. */
function copyableText(value: JsonValue): string {
  return typeof value === "string" ? value : (JSON.stringify(value, null, 2) ?? "null");
}

function CopyableCell({ entry, text }: { entry?: JsonValue; text?: string }) {
  const { copied, copy } = useCopyToClipboard(text ?? copyableText(entry as JsonValue));
  return (
    <button
      type="button"
      onClick={copy}
      title={copied ? "Copied" : "Copy"}
      className="group flex w-full min-w-0 items-start gap-1.5 text-left font-mono text-muted-foreground hover:text-foreground"
    >
      <TruncatedValue className="min-w-0 flex-1">
        {text ?? valueSummary(entry as JsonValue)}
      </TruncatedValue>
      <span
        className={cn(
          "mt-0.5 shrink-0",
          copied ? "opacity-100" : "opacity-0 group-hover:opacity-100",
        )}
        aria-hidden="true"
      >
        {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
      </span>
      <span className="sr-only">{copied ? "Copied" : "Copy"}</span>
    </button>
  );
}

function jsonEntries(value: JsonValue): Array<[string, JsonValue]> {
  if (Array.isArray(value)) return value.map((item, index) => [`[${index}]`, item]);
  if (isNested(value)) return Object.entries(value);
  return [["value", value]];
}

export function JsonValueTable({ value, depth = 0 }: { value: JsonValue; depth?: number }) {
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(() => new Set());

  return (
    <table
      className="w-full min-w-[22rem] border-collapse text-left text-xs"
      data-json-table-depth={depth}
    >
      <thead className="border-b border-border bg-muted/40 text-muted-foreground">
        <tr>
          <th className="w-[38%] min-w-[8rem] px-3 py-2 font-medium">Key</th>
          <th className="min-w-[10rem] px-3 py-2 font-medium">Value</th>
        </tr>
      </thead>
      <tbody>
        {jsonEntries(value).map(([key, entry]) => {
          const expandable = isNested(entry) && depth < 2;
          const expanded = expandedKeys.has(key);
          return (
            <JsonValueRow
              key={key}
              entry={entry}
              entryKey={key}
              expandable={expandable}
              expanded={expanded}
              onToggle={() =>
                setExpandedKeys((current) => {
                  const next = new Set(current);
                  if (next.has(key)) next.delete(key);
                  else next.add(key);
                  return next;
                })
              }
              depth={depth}
            />
          );
        })}
      </tbody>
    </table>
  );
}

function JsonValueRow({
  entry,
  entryKey,
  expandable,
  expanded,
  onToggle,
  depth,
}: {
  entry: JsonValue;
  entryKey: string;
  expandable: boolean;
  expanded: boolean;
  onToggle: () => void;
  depth: number;
}) {
  return (
    <>
      <tr className="border-b border-border/60 last:border-b-0">
        <th
          scope="row"
          className="min-w-[8rem] px-3 py-2 align-top font-mono font-medium text-foreground"
        >
          {expandable ? (
            <button
              type="button"
              onClick={onToggle}
              aria-expanded={expanded}
              aria-label={`${expanded ? "Collapse" : "Expand"} ${entryKey}`}
              className="flex w-full min-w-0 items-center gap-1 text-left hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <ChevronRight
                className={cn("size-3 shrink-0 transition-transform", expanded && "rotate-90")}
              />
              <TruncatedValue className="min-w-0">{entryKey}</TruncatedValue>
            </button>
          ) : (
            <CopyableCell text={entryKey} />
          )}
        </th>
        <td className="min-w-[10rem] px-3 py-2 align-top">
          <CopyableCell entry={entry} />
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-border/60 bg-muted/20">
          <td colSpan={2} className="p-2">
            <div className="overflow-x-auto rounded border border-border/70 bg-background">
              <JsonValueTable value={entry} depth={depth + 1} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/** Code ⇄ Table switch. `className` lets each host match its own button chrome. */
export function JsonViewToggle({
  view,
  onToggle,
  className,
}: {
  view: "code" | "table";
  onToggle: () => void;
  className?: string;
}) {
  return (
    <button type="button" onClick={onToggle} aria-pressed={view === "table"} className={className}>
      <TableProperties className="size-3.5" />
      {view === "table" ? "Code" : "Table"}
    </button>
  );
}
